import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { ZoomOutMap, FullscreenExit, ZoomIn, ZoomOut } from '@material-ui/icons';
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs';

const styles = {
  container: (isFullScreen) => ({
    backgroundColor: '#1D1F21',
    height: isFullScreen ? '100vh' : '65vh',
    overflow: 'auto',
    position: 'relative',
    transition: 'height 0.3s ease',
  }),
  zoomContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  zoomWrapper: {
    display: 'block',
  },
  controls: (visible) => ({
    position: 'sticky',
    bottom: '8px',
    margin: 'auto',
    backgroundColor: 'rgba(33, 33, 33, 0.8)',
    color: 'white',
    padding: '0 10px',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.4s ease',
    zIndex: 10,
    pointerEvents: visible ? 'auto' : 'none',
    width: 'fit-content',
  }),
  pageWrapper: {
    display: 'block',
    margin: '10px auto',
    overflowX: 'auto',
  },
  canvas: {
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 0 6px rgba(0,0,0,0.15)',
    pointerEvents: 'none',
    userSelect: 'none',
    maxWidth: '100%',
    height: 'auto',
  },
  input: {
    width: '50px',
    textAlign: 'center',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '4px',
  },
  button: {
    padding: '5px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '12px',
  },
};

const PDFViewer = ({ url, initialScale = 1.2, onLoad = () => null }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(initialScale);
  const [currentPage, setCurrentPage] = useState(1);
  const [inputValue, setInputValue] = useState('1');
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [visiblePages, setVisiblePages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const canvasRefs = useRef([]);
  const pageRefs = useRef([]);
  const containerRef = useRef(null);
  const hideTimer = useRef(null);
  const isManuallyScrolling = useRef(false);
  const renderTasks = useRef([]);
  const lastScrollTime = useRef(0);

  /**  Load PDF */
  useEffect(() => {
    const loadPDF = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const loadingTask = pdfjsLib.getDocument({ url });
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        canvasRefs.current = new Array(pdf.numPages).fill(null);
        pageRefs.current = new Array(pdf.numPages).fill(null);
        setVisiblePages(new Set([1, 2])); // Render first two pages initially
        onLoad();
      } catch (error) {
        setHasError(true);
        console.error('Error loading PDF:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (url) loadPDF();

    //  capture the current value in a local variable
    const currentRenderTasks = renderTasks.current;

    return () => {
      // Cleanup render tasks
      currentRenderTasks.forEach((task) => {
        if (task)
          try {
            task.cancel();
          } catch (e) {}
      });
    };
  }, [url]);

  // Progressive render
  useEffect(() => {
    const renderPage = async (pageNum) => {
      if (!pdfDoc || pageNum < 1 || pageNum > numPages || !canvasRefs.current[pageNum - 1]) return;

      const canvas = canvasRefs.current[pageNum - 1];
      const context = canvas.getContext('2d');

      // Cancel previous render task if exists
      if (renderTasks.current[pageNum]) {
        try {
          renderTasks.current[pageNum].cancel();
        } catch {}
      }

      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });

      // Set canvas dimensions
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Clear canvas
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Create new task
      const renderTask = page.render({ canvasContext: context, viewport });
      renderTasks.current[pageNum] = renderTask;

      try {
        await renderTask.promise;
      } catch (error) {
        if (error?.name !== 'RenderingCancelledException') {
          console.error(`Error rendering page ${pageNum}:`, error);
        }
      } finally {
        renderTasks.current[pageNum] = null;
      }
    };

    const pagesToRender = new Set();
    visiblePages.forEach((pageNum) => {
      pagesToRender.add(pageNum);
      if (pageNum > 1) pagesToRender.add(pageNum - 1);
      if (pageNum < numPages) pagesToRender.add(pageNum + 1);
    });

    // Render only visible + 1 page before and after
    Array.from(pagesToRender).forEach((pageNum) => {
      if (pageNum >= 1 && pageNum <= numPages) {
        requestIdleCallback(() => renderPage(pageNum));
      }
    });
  }, [visiblePages, scale, pdfDoc, numPages]);

  // Sync current page with input value
  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  // Handle scroll to page when currentPage changes
  useEffect(() => {
    if (isManuallyScrolling.current) return;

    const ref = pageRefs.current[currentPage - 1];
    if (ref && ref.scrollIntoView) {
      // Use smooth scroll only if not triggered by the observer
      const behavior = Date.now() - lastScrollTime.current > 1000 ? 'smooth' : 'auto';
      ref.scrollIntoView({ behavior, block: 'start' });
    }
  }, [currentPage]);

  // Show/hide controls on mouse movement
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const showControls = () => {
      setControlsVisible(true);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    };

    container.addEventListener('mousemove', showControls);
    container.addEventListener('mouseleave', () => setControlsVisible(false));

    return () => {
      container.removeEventListener('mousemove', showControls);
      container.removeEventListener('mouseleave', () => setControlsVisible(false));
      clearTimeout(hideTimer.current);
    };
  }, []);

  // Intersection Observer for visible pages
  useEffect(() => {
    if (!containerRef.current || numPages === 0) return;

    const visible = new Set();
    let scrollTimeout;

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const index = pageRefs.current.findIndex((el) => el === entry.target);
        if (index !== -1) {
          if (entry.isIntersecting) {
            visible.add(index + 1);
          } else {
            visible.delete(index + 1);
          }
        }
      });

      setVisiblePages(new Set(visible));

      // Update current page based on most visible page
      if (!isManuallyScrolling.current && visible.size > 0) {
        const sortedVisible = Array.from(visible).sort((a, b) => {
          const aRect = pageRefs.current[a - 1]?.getBoundingClientRect();
          const bRect = pageRefs.current[b - 1]?.getBoundingClientRect();
          if (!aRect || !bRect) return 0;
          return Math.abs(aRect.top) - Math.abs(bRect.top);
        });

        if (sortedVisible.length > 0) {
          const mostVisible = sortedVisible[0];
          lastScrollTime.current = Date.now();
          setCurrentPage((prev) => (mostVisible !== prev ? mostVisible : prev));
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: containerRef.current,
      threshold: 0.5,
      rootMargin: '50px 0px 50px 0px', // Add some margin to detect nearby pages
    });

    // Observe all page elements
    pageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      clearTimeout(scrollTimeout);
    };
  }, [numPages]);

  const zoomIn = () =>
    setScale((prev) => {
      const newScale = Math.min(prev + 0.2, 3);
      return newScale;
    });

  const zoomOut = () =>
    setScale((prev) => {
      const newScale = Math.max(prev - 0.2, 0.5);
      return newScale;
    });

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  // Handle fullscreen changes (e.g., ESC)
  useEffect(() => {
    const handleFullScreenChange = () => setIsFullScreen(!!document.fullscreenElement);

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleInputBlur = () => {
    const val = parseInt(inputValue);
    if (!isNaN(val) && val >= 1 && val <= numPages) {
      isManuallyScrolling.current = true;
      setCurrentPage(val);
      setTimeout(() => {
        isManuallyScrolling.current = false;
      }, 1000);
    } else {
      setInputValue(String(currentPage)); // reset to current page if invalid
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  if (isLoading) return null;

  if (hasError) {
    return (
      <div
        style={{
          color: 'white',
          backgroundColor: '#1D1F21',
          height: '65vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px',
        }}
      >
        Failed to load PDF file
      </div>
    );
  }

  return (
    <div ref={containerRef} style={styles.container(isFullScreen)}>
      {/* Pages with vertical scroll */}
      <div style={styles.zoomContainer}>
        <div style={styles.zoomWrapper}>
          {Array.from({ length: numPages }).map((_, index) => (
            <div
              key={`page-${index}`}
              ref={(el) => (pageRefs.current[index] = el)}
              style={styles.pageWrapper}
              data-page-number={index + 1}
            >
              <canvas
                ref={(el) => (canvasRefs.current[index] = el)}
                style={styles.canvas}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          ))}
        </div>
      </div>
      {/* Controls */}
      <div style={styles.controls(controlsVisible)}>
        <span>
          Page{' '}
          <input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            min={1}
            max={numPages}
            style={styles.input}
          />
          {''} / {numPages}
        </span>
        <button style={styles.button} onClick={zoomOut} disabled={scale <= 0.5}>
          <ZoomOut />
        </button>
        <button style={styles.button} onClick={zoomIn} disabled={scale >= 3}>
          <ZoomIn />
        </button>
        <button style={styles.button} onClick={toggleFullScreen}>
          {isFullScreen ? <FullscreenExit /> : <ZoomOutMap />}
        </button>
      </div>
    </div>
  );
};

export default PDFViewer;
